<template>
    <li ref="menuItem">
        <router-link :to="{name: router.name}" v-if="router">
            <div class="menu" :class="{menuActive: router.name == menuActiveName || subMenuActive || hasActiveChild}" @click="changeMenu">
                <sw-icon :name="icon" class="menu_icon" /> 
                <span> {{ name }} </span>
                
                <div class="menuSubMenu" v-if="isSubMenu" @click.prevent="showSubMenu">
                    <div :class="{iconMenuSubMenuActive: subMenuActive}">></div>
                </div>
            </div>
        </router-link>

        <div v-else>
            <div class="menu" :class="{menuActive: subMenuActive || hasActiveChild}" @click="changeMenu">
                <sw-icon :name="icon" class="menu_icon" />
                <span> {{ name }} </span>

                <div class="menuSubMenu" v-if="isSubMenu" @click.prevent="showSubMenu">
                    <div :class="{iconMenuSubMenuActive: subMenuActive}">></div>
                </div>
            </div>
        </div>
        
        <transition name="menuFade">
            <ul v-show="isSubMenu && subMenuActive">
                <slot></slot>
            </ul>
        </transition>
    </li>
</template>

<script setup>
    import { ref, computed, inject, onMounted, provide, watch } from 'vue';
    import { useStore } from 'vuex';

    const props = defineProps({
        name: String,
        router: Object,
        icon: String,
        isSubMenu: Boolean
    });

    // Store e estado reativo
    const store = useStore();
    const subMenuActive = ref(false);
    const hasActiveChild = ref(false);
    const currentMenuRef = ref(null);

    // Computed properties
    const menuActiveName = computed(() => store.state.menuActiveName);

    // Injeções de dependência
    const closeOtherSubMenus = inject('closeOtherSubMenus', null);
    const registerSubMenu = inject('registerSubMenu', null);

    // Função para ativar este menu quando um filho estiver ativo
    const activateParentMenu = () => {
        hasActiveChild.value = true;
        subMenuActive.value = true;

        // Fechar outros submenus quando este for ativado, exceto este
        if (closeOtherSubMenus && currentMenuRef.value) {
            closeOtherSubMenus(currentMenuRef.value);
        }
    };

    // Prover função para os SubMenuComponents filhos
    provide('activateParentMenu', activateParentMenu);

    // Watchers
    watch(hasActiveChild, (newValue) => {
        if (newValue && props.isSubMenu) {
            subMenuActive.value = true;
        }
    });

    // Event handlers
    const changeMenu = () => {
        if (!props.isSubMenu && closeOtherSubMenus) {
            closeOtherSubMenus();
        }
        // Fechar menu mobile apenas quando navegar para um link real (não submenu)
        if (window.innerWidth <= 768 && props.router && !props.isSubMenu) {
            store.commit('closeMobileMenu');
        }
    };

    const showSubMenu = () => {
        subMenuActive.value = !subMenuActive.value;
    };

    // Lifecycle hooks
    onMounted(() => {
        if (props.isSubMenu && registerSubMenu) {
            const menuRef = {
                closeSubMenu: () => {
                    subMenuActive.value = false;
                    hasActiveChild.value = false;
                }
            };
            currentMenuRef.value = menuRef;
            registerSubMenu(menuRef);
        }
    });
</script>

<style lang="scss" scoped>
    .menu{
        position: relative;
        width: 100%;
        padding: 1em 1.25em;
        border-radius: var(--borderRadiusMedium);
        cursor: pointer;
        transition: var(--transitionDefault);
        font-size: var(--fontSizeSm);
        font-weight: var(--fontWeightMedium);
        letter-spacing: 0.025em;
        display: flex;
        align-items: center;
        background: transparent;
        border: 1px solid transparent;
        margin-bottom: 0.25em;
        
        &:hover:not(.menuActive){
            background: var(--backgroundHover);
            border-color: var(--borderColorLight);
            transform: translateX(0.25em);
            box-shadow: var(--boxShadowXs);
        }
    }
    
    .menuActive{
        background: linear-gradient(135deg, var(--linkColor), var(--colorBlueDark100));
        color: var(--textWhite);
        font-weight: var(--fontWeightSemibold);
        z-index: 2;
        border-color: var(--linkColor);
        box-shadow: var(--boxShadowMedium);
        transform: translateX(0.5em);
        
        & .menuSubMenu{
            background: rgba(255, 255, 255, 0.2);
            color: var(--textWhite);
            
            & > div{
                color: var(--textWhite);
            }
        }
        
        & .menu_icon{
            color: var(--textWhite);
        }
    }

    .menu_icon{
        margin-right: 0.75em;
        font-size: 1.125em;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 1.5em;
        height: 1.5em;
        color: var(--textSecondary);
        transition: var(--transitionDefault);
        flex-shrink: 0;
    }
    
    .menuActive .menu_icon{
        color: var(--textWhite);
    }

    .menuSubMenu{
        position: absolute;
        top: 50%;
        right: 1.25em;
        padding: 0.5em 0.75em;
        background: var(--backgroundInsideBox);
        border-radius: var(--borderRadiusSmall);
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 1.5em;
        min-height: 1.5em;
        transition: var(--transitionDefault);
        
        & > div{
            font-weight: var(--fontWeightBold);
            font-size: 0.875em;
            color: var(--textSecondary);
            transition: var(--transitionDefault);
        }
        
        &:hover{
            background: var(--linkColor);
            
            & > div{
                color: var(--textWhite);
            }
        }
    }

    .iconMenuSubMenuActive{
        transition: var(--transitionDefault);
        transform: rotate(90deg);
    }

    .menuFade-enter-active, .menuFade-leave-active {
        transition: var(--transitionSlow);
    }
    .menuFade-enter-from, .menuFade-leave-to{
        opacity: 0;
        transform: translateY(-0.5em);
    }
    .menuFade-enter-to, .menuFade-leave-from{
        opacity: 1;
        transform: translateY(0);
    }
</style>